import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { usePermissions } from '@shared/hooks/usePermissions';
import { apiClientWH } from '@shared/services/ApiClient';
import { ShopifyRedirectApiAdapter } from '../../adapters/ShopifyRedirectApiAdapter';
import { TikTokRedirectApiAdapter } from '../../adapters/TikTokRedirectApiAdapter';
import { AmazonFbmSettingsModal, AmazonFbmSettingsModalRef } from '../modals/AmazonFbmSettingsModal';
import { ManualShopSettingsModal, ManualShopSettingsModalRef } from '../modals/ManualShopSettingsModal';
import { WooCommerceSettingsModal, WooCommerceSettingsModalRef } from '../modals/WooCommerceSettingsModal';

interface AddShopButtonProps {
  onShopCreated?: () => void;
}

/**
 * Componente para el botón de añadir tienda
 * Permite seleccionar el tipo de tienda a crear (Shopify, TikTok, WooCommerce, Manual)
 * Maneja permisos de creación
 */
export const AddShopButton = ({ onShopCreated }: AddShopButtonProps) => {
  const { t } = useTranslation('shopsModule');
  const { toast } = useToast();
  const { canAccess } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const amazonFbmModalRef = useRef<AmazonFbmSettingsModalRef>(null);
  const wooCommerceModalRef = useRef<WooCommerceSettingsModalRef>(null);
  const manualShopModalRef = useRef<ManualShopSettingsModalRef>(null);

  // Verificar si el usuario puede crear tiendas
  const canCreate = canAccess(['tenant', 'tenant_external'], ['shops.create']);

  const handleShopifyRedirect = async () => {
    setIsLoading(true);
    try {
      const adapter = new ShopifyRedirectApiAdapter(createHttpClient(apiClientWH));
      const redirectUrl = await adapter.getRedirectUrl();

      if (redirectUrl) {
        window.open(redirectUrl, '_blank');
      } else {
        throw new Error('La URL de redirección no es válida');
      }
    } catch (error: any) {
      console.error('Error al intentar redirigir a Shopify:', error);
      toast({
        title: t('toasts.error.title'),
        description: error.message || t('toasts.error.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTikTokRedirect = async () => {
    setIsLoading(true);
    try {
      const adapter = new TikTokRedirectApiAdapter(createHttpClient(apiClientWH));
      const redirectUrl = await adapter.getRedirectUrl();

      if (redirectUrl) {
        window.open(redirectUrl, '_blank');
      } else {
        throw new Error('La URL de redirección no es válida');
      }
    } catch (error: any) {
      console.error('Error al intentar redirigir a TikTok:', error);
      toast({
        title: t('toasts.error.title'),
        description: error.message || t('toasts.error.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChannel = (channel: 'AmazonFBM' | 'WooCommerce' | 'Manual' | 'Shopify' | 'TikTok') => {
    if (channel === 'Shopify') {
      handleShopifyRedirect();
    } else if (channel === 'TikTok') {
      handleTikTokRedirect();
    } else if (channel === 'WooCommerce') {
      wooCommerceModalRef.current?.open();
    } else if (channel === 'AmazonFBM') {
      amazonFbmModalRef.current?.open();
    } else {
      manualShopModalRef.current?.open();
    }
  };

  // Si no tiene permisos, no mostrar el botón
  if (!canCreate) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="primary" size="sm" data-testid="add-shop-button" loading={isLoading} disabled={isLoading}>
            {isLoading ? t('common.loading') : t('buttons.create')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          <DropdownMenuItem onClick={() => handleSelectChannel('Shopify')} asChild disabled={isLoading}>
            <div className="flex justify-between items-center w-full cursor-pointer">
              <span>Shopify</span>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3.5 h-3.5 text-icons" />
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelectChannel('TikTok')} asChild disabled={isLoading}>
            <div className="flex justify-between items-center w-full cursor-pointer">
              <span>TikTok</span>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="w-3.5 h-3.5 text-icons" />
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelectChannel('WooCommerce')} disabled={isLoading}>
            WooCommerce
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelectChannel('AmazonFBM')} disabled={isLoading}>
            {t('filters.channels.amazonFbm')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSelectChannel('Manual')} disabled={isLoading}>
            Tienda Manual
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AmazonFbmSettingsModal ref={amazonFbmModalRef} onShopCreated={onShopCreated} />
      <WooCommerceSettingsModal ref={wooCommerceModalRef} onShopCreated={onShopCreated} />
      <ManualShopSettingsModal ref={manualShopModalRef} onShopCreated={onShopCreated} />
    </>
  );
};
